import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { parseNote } from '@rbv/shared';
import { useApp } from '../store.js';
import { vault } from '../sync/vault.js';
import { index } from '../worker/client.js';
import type { TagNode } from '../worker/api.js';
import { openDailyNote } from '../features/index.js';
import { getRecents, useVaultFiles } from './hooks.js';
import { Icon } from '../mo/Icon.js';
import { Panel } from '../mo/Panel.js';

type OpenFn = (id: string, newTab: boolean) => void;

/** A clickable note row: title + muted folder subtitle. ⌘/ctrl/middle → new tab. */
function NoteRow({ id, open }: { id: string; open: OpenFn }) {
  const entry = vault.getEntry(id);
  if (!entry || entry.deleted) return null;
  const title = vault.titleOf(id) || 'Untitled';
  const slash = entry.path.lastIndexOf('/');
  const folder = slash > 0 ? entry.path.slice(0, slash) : '';
  return (
    <button
      className="home-row"
      title={entry.path}
      onClick={(e) => open(id, e.metaKey || e.ctrlKey)}
      onAuxClick={(e) => {
        if (e.button === 1) {
          e.preventDefault();
          open(id, true);
        }
      }}
    >
      <span className="home-row-title">{title}</span>
      {folder && <span className="home-row-sub">{folder}</span>}
    </button>
  );
}


/** The landing dashboard (shown for an empty tab). Entirely client-side — reads
 * the locally-replicated vault, so it works offline with no server round-trip. */
export function Home() {
  const openNote = useApp((s) => s.openNote);
  const setQuickSwitcherOpen = useApp((s) => s.setQuickSwitcherOpen);
  const setAssistantOpen = useApp((s) => s.setAssistantOpen);
  const setSidebarTab = useApp((s) => s.setSidebarTab);
  const pinnedIds = useApp((s) => s.pinnedIds);
  const files = useVaultFiles();

  const open: OpenFn = (id, newTab) => openNote(id, newTab ? { newTab: true } : undefined);
  const liveNote = (id: string) => {
    const e = files.get(id);
    return e && e.kind === 'note' && !e.deleted;
  };

  const notes = useMemo(
    () => [...files.entries()].filter(([, e]) => e.kind === 'note' && !e.deleted),
    [files],
  );
  const byMtime = useMemo(() => [...notes].sort((a, b) => b[1].mtime - a[1].mtime), [notes]);

  const recents = useMemo(() => getRecents().filter(liveNote).slice(0, 8), [files]);
  const pinned = useMemo(() => pinnedIds.filter(liveNote).slice(0, 10), [pinnedIds, files]);

  const onThisDay = useMemo(() => {
    const now = new Date();
    return notes
      .filter(([, e]) => {
        const d = new Date(e.ctime);
        return (
          d.getMonth() === now.getMonth() &&
          d.getDate() === now.getDate() &&
          d.getFullYear() !== now.getFullYear()
        );
      })
      .sort((a, b) => b[1].ctime - a[1].ctime)
      .slice(0, 6)
      .map(([id]) => id);
  }, [notes]);

  // Recently ingested from email — bounded frontmatter scan over newest notes.
  const [emailNotes, setEmailNotes] = useState<string[]>([]);
  useEffect(() => {
    const out: string[] = [];
    for (const [id] of byMtime.slice(0, 120)) {
      try {
        const fm = parseNote(vault.noteContent(id)).frontmatter;
        if (fm && fm.source === 'email') out.push(id);
      } catch {
        /* skip unreadable */
      }
      if (out.length >= 6) break;
    }
    setEmailNotes(out);
  }, [byMtime]);

  // Top tags (async, from the index worker).
  const [tags, setTags] = useState<{ tag: string; count: number }[]>([]);
  useEffect(() => {
    let stale = false;
    void index.tags().then((tree) => {
      if (stale) return;
      const flat: { tag: string; count: number }[] = [];
      const walk = (nodes: TagNode[]) => {
        for (const n of nodes) {
          flat.push({ tag: n.tag, count: n.count });
          if (n.children?.length) walk(n.children);
        }
      };
      walk(tree);
      setTags(flat.sort((a, b) => b.count - a.count).slice(0, 20));
    });
    return () => {
      stale = true;
    };
  }, [files]);

  const openTags = () => {
    setSidebarTab('tags');
    useApp.getState().setSidebarOpen(true);
  };

  return (
    <div className="home">
      <div className="home-hero">
        <h1>Your notes</h1>
        <div className="home-hero-actions">
          <button className="home-hero-btn primary" onClick={() => setQuickSwitcherOpen(true)}>
            <Icon name="search" size={15} /> Search &amp; jump
          </button>
          <button className="home-hero-btn" onClick={() => setAssistantOpen(true)}>
            <Icon name="spark" size={15} /> Ask my notes
          </button>
          <button className="home-hero-btn" onClick={() => void openDailyNote()}>
            <Icon name="agenda" size={15} /> Today&rsquo;s daily note
          </button>
          <button
            className="home-hero-btn"
            onClick={() => void vault.createNote('Untitled.md').then((id) => openNote(id))}
          >
            ＋ New note
          </button>
        </div>
      </div>

      {/*
        BUILT FROM THE DECK'S OWN PANEL, not from a card this page invented.
        `home-card` carried a title and a body; `Panel` carries an icon, a hue, a caption and a
        count, and every panel on the deck has looked like that for weeks. Same tokens either way,
        so the colours never differed: what differed was the SHAPE, which is why the previous pass
        of CSS devices could not close the gap. Each section keeps its own semantic hue rather than
        sharing one, exactly as the deck's modules do.
      */}
      <div className="home-grid">
        {recents.length > 0 && (
          <Panel
            icon="clock"
            hue="time"
            name="Continue"
            caption="Where you left off"
            headline={`${recents.length}`}
            sub="open again"
            defaultOpen
          >
            {recents.map((id) => (
              <NoteRow key={id} id={id} open={open} />
            ))}
          </Panel>
        )}
        {pinned.length > 0 && (
          <Panel
            icon="star"
            hue="intent"
            name="Pinned"
            caption="Kept deliberately"
            headline={`${pinned.length}`}
            sub="pinned"
            defaultOpen
          >
            {pinned.map((id) => (
              <NoteRow key={id} id={id} open={open} />
            ))}
          </Panel>
        )}
        {byMtime.length > 0 && (
          <Panel
            icon="notes"
            hue="know"
            name="Recently edited"
            caption="Changed most recently"
            headline={`${byMtime.length}`}
            sub="touched"
            defaultOpen
          >
            {byMtime.slice(0, 8).map(([id]) => (
              <NoteRow key={id} id={id} open={open} />
            ))}
          </Panel>
        )}
        {emailNotes.length > 0 && (
          <Panel
            icon="inbox"
            hue="waiting"
            name="From email"
            caption="Arrived rather than written"
            headline={`${emailNotes.length}`}
            sub="ingested"
            defaultOpen
          >
            {emailNotes.map((id) => (
              <NoteRow key={id} id={id} open={open} />
            ))}
          </Panel>
        )}
        {onThisDay.length > 0 && (
          <Panel
            icon="agenda"
            hue="time"
            name="On this day"
            caption="A year ago and further back"
            headline={`${onThisDay.length}`}
            sub="from earlier years"
            defaultOpen
          >
            {onThisDay.map((id) => (
              <NoteRow key={id} id={id} open={open} />
            ))}
          </Panel>
        )}
        {tags.length > 0 && (
          <Panel
            icon="tag"
            hue="intent"
            name="Tags"
            caption="How the vault is labelled"
            headline={`${tags.length}`}
            sub="in use"
            defaultOpen
          >
            <div className="home-tags">
              {tags.map((t) => (
                <button key={t.tag} className="home-tag" onClick={openTags} title={`${t.count} notes`}>
                  #{t.tag} <span className="home-tag-count">{t.count}</span>
                </button>
              ))}
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}
